import { decimal, maxValue, minValue, numeric, required, requiredIf } from 'vuelidate/lib/validators';
import type {
  IntRangeRightBoundOptional,
  JobsModeratorsEmployer,
  JobsModeratorsField,
  JobsModeratorsFieldsMultiSelectRecord,
  JobsModeratorsFieldsSingleSelectRecord,
} from '~/types/graphql';
import { JobsFieldsRecordType, JobsModeratorsEmployerPlan, JobsVacancyEmployment } from '~/types/graphql';
import type { Field, FieldDefaultValue, FieldValue } from '~/types/store/create-vacancy';
import { CreateVacancyForm } from '~/types/store/create-vacancy';

export enum JobsModeratorsFieldsComponents {
  'BravadoCheckboxV3' = 'BravadoCheckboxV3',
  'BravadoTextInputV3' = 'BravadoTextInputV3',
  'JobsBackOfficeRangeInput' = 'JobsBackOfficeRangeInput',
  'BravadoSelectV3' = 'BravadoSelectV3',
  'JobsBackOfficeCitiesSelect' = 'JobsBackOfficeCitiesSelect',
  'JobsBackOfficeCompaniesAutocomplete' = 'JobsBackOfficeCompaniesAutocomplete',
  'JobsBackOfficeDepartmentSelect' = 'JobsBackOfficeDepartmentSelect',
  'JobsBackOfficeIndustriesSelect' = 'JobsBackOfficeIndustriesSelect',
  'BravadoAddMultipleInput' = 'BravadoAddMultipleInput',
}

export enum JobsStaticFieldsType {
  'JobsStaticFieldsRangeSlider' = 'JobsStaticFieldsRangeSlider',
  'JobsStaticFieldsToggle' = 'JobsStaticFieldsToggle',
}
export enum JobsModeratorsFieldsType {
  'JobsModeratorsFieldsFloat' = 'JobsModeratorsFieldsFloat',
  'JobsModeratorsFieldsInteger' = 'JobsModeratorsFieldsInteger',
  'JobsModeratorsFieldsBoolean' = 'JobsModeratorsFieldsBoolean',
  'JobsModeratorsFieldsString' = 'JobsModeratorsFieldsString',
  'JobsModeratorsFieldsFloatRange' = 'JobsModeratorsFieldsFloatRange',
  'JobsModeratorsFieldsIntegerRange' = 'JobsModeratorsFieldsIntegerRange',
  'JobsModeratorsFieldsMultiSelect' = 'JobsModeratorsFieldsMultiSelect',
  'JobsModeratorsFieldsMultiSelectRecord' = 'JobsModeratorsFieldsMultiSelectRecord',
  'JobsModeratorsFieldsSingleSelect' = 'JobsModeratorsFieldsSingleSelect',
  'JobsModeratorsFieldsSingleSelectRecord' = 'JobsModeratorsFieldsSingleSelectRecord',
  'JobsModeratorsFieldsStringArray' = 'JobsModeratorsFieldsStringArray',
}

export enum JobsModeratorsFieldsFormGroupKey {
  'floatFields' = 'floatFields',
  'integerFields' = 'integerFields',
  'booleanFields' = 'booleanFields',
  'stringFields' = 'stringFields',
  'floatRangeFields' = 'floatRangeFields',
  'integerRangeFields' = 'integerRangeFields',
  'multiSelectFields' = 'multiSelectFields',
  'multiSelectRecordFields' = 'multiSelectRecordFields',
  'singleSelectFields' = 'singleSelectFields',
  'singleSelectRecordFields' = 'singleSelectRecordFields',
  'stringArrayFields' = 'stringArrayFields',
}

export enum PossibleValueIdentifiers {
  'value' = 'value',
  'values' = 'values',
  'recordId' = 'recordId',
  'recordIds' = 'recordIds',
}

export const getFieldDefaultValue = (
  fieldType: JobsModeratorsField['__typename'] | JobsStaticFieldsType,
): FieldDefaultValue => {
  if (!fieldType) return '';

  return {
    [JobsModeratorsFieldsType.JobsModeratorsFieldsFloat]: 0,
    [JobsModeratorsFieldsType.JobsModeratorsFieldsInteger]: 0,
    [JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean]: false,
    [JobsModeratorsFieldsType.JobsModeratorsFieldsString]: '',
    [JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange]: { min: '', max: '' },
    [JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange]: { min: null, max: null },
    [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect]: [],
    [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord]: [],
    [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect]: null,
    [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelectRecord]: [],
    [JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray]: [''],
    [JobsStaticFieldsType.JobsStaticFieldsRangeSlider]: '0',
    [JobsStaticFieldsType.JobsStaticFieldsToggle]: false,
  }[fieldType];
};

function validateInputRangeRightBoundOptional(
  formValues: IntRangeRightBoundOptional,
  bounds?: IntRangeRightBoundOptional,
): Record<string, unknown> {
  const minValueValidations = bounds?.min ? { minValue: minValue(bounds.min) } : {};
  const maxValueValidations = bounds?.max ? { maxValue: maxValue(bounds.max) } : {};

  return {
    min: { required, ...minValueValidations },
    max: {
      minValue: minValue(Number(formValues?.min)),
      ...maxValueValidations,
    },
  };
}

export const getFieldValidationRules = (
  { slug, type, requiredForRoles }: Field,
  form: CreateVacancyForm,
  selectedDynamicFields: Array<Field>,
  employer: JobsModeratorsEmployer | null,
) => {
  const validations = {} as Record<string, unknown>;
  const isSelectedDynamicField = selectedDynamicFields.some(dynamicField => dynamicField.slug === slug);

  if (isSelectedDynamicField || requiredForRoles.some(item => form.role.includes(item))) {
    validations.required = required;

    if (
      [
        JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange,
        JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange,
      ].includes(type ?? '')
    ) {
      Object.assign(validations, validateInputRangeRightBoundOptional(form[slug] as IntRangeRightBoundOptional));
    }
  }

  if (type === JobsModeratorsFieldsType.JobsModeratorsFieldsInteger) {
    validations.numeric = numeric;
  }

  if (type === JobsModeratorsFieldsType.JobsModeratorsFieldsFloat) {
    validations.decimal = decimal;
  }

  if (slug === 'matchScoreThreshold') {
    validations.minValue = minValue(0);
    validations.maxValue = maxValue(1);
  }

  if (slug === 'cities') {
    validations.required = requiredIf(() => !form.countries.length);
  }

  if (type === JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray) {
    validations.hasAtLeastOneFilledField = (value: Array<string>) => {
      return value.some(fieldValue => !!fieldValue);
    };
  }

  if (slug === 'contractId') {
    validations.required = requiredIf(() =>
      Boolean(employer?.plan === JobsModeratorsEmployerPlan.Full && employer.activeContracts?.length),
    );
  }

  return validations;
};

const componentsForRecord = {
  [JobsFieldsRecordType.City]: JobsModeratorsFieldsComponents.JobsBackOfficeCitiesSelect,
  [JobsFieldsRecordType.Company]: JobsModeratorsFieldsComponents.JobsBackOfficeCompaniesAutocomplete,
  [JobsFieldsRecordType.Department]: JobsModeratorsFieldsComponents.JobsBackOfficeDepartmentSelect,
  [JobsFieldsRecordType.Industry]: JobsModeratorsFieldsComponents.JobsBackOfficeIndustriesSelect,
};

export const JobsModeratorsFieldsResolvers = {
  JobsModeratorsFieldsBoolean: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoCheckboxV3;
    },
  },
  JobsModeratorsFieldsFloat: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoTextInputV3;
    },
  },
  JobsModeratorsFieldsFloatRange: {
    component() {
      return JobsModeratorsFieldsComponents.JobsBackOfficeRangeInput;
    },
  },
  JobsModeratorsFieldsInteger: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoTextInputV3;
    },
  },
  JobsModeratorsFieldsIntegerRange: {
    component() {
      return JobsModeratorsFieldsComponents.JobsBackOfficeRangeInput;
    },
  },
  JobsModeratorsFieldsMultiSelect: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoSelectV3;
    },
  },
  JobsModeratorsFieldsMultiSelectRecord: {
    component(field: JobsModeratorsFieldsMultiSelectRecord) {
      return componentsForRecord[field.recordType];
    },
  },
  JobsModeratorsFieldsSingleSelect: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoSelectV3;
    },
  },
  JobsModeratorsFieldsSingleSelectRecord: {
    component(field: JobsModeratorsFieldsSingleSelectRecord) {
      return componentsForRecord[field.recordType];
    },
  },
  JobsModeratorsFieldsString: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoTextInputV3;
    },
  },
  JobsModeratorsFieldsStringArray: {
    component() {
      return JobsModeratorsFieldsComponents.BravadoAddMultipleInput;
    },
  },
};

export const FormKeyForFieldType: Record<JobsModeratorsFieldsType, JobsModeratorsFieldsFormGroupKey> = {
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloat]: JobsModeratorsFieldsFormGroupKey.floatFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsInteger]: JobsModeratorsFieldsFormGroupKey.integerFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean]: JobsModeratorsFieldsFormGroupKey.booleanFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsString]: JobsModeratorsFieldsFormGroupKey.stringFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange]: JobsModeratorsFieldsFormGroupKey.floatRangeFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange]: JobsModeratorsFieldsFormGroupKey.integerRangeFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect]: JobsModeratorsFieldsFormGroupKey.multiSelectFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord]:
    JobsModeratorsFieldsFormGroupKey.multiSelectRecordFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect]: JobsModeratorsFieldsFormGroupKey.singleSelectFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelectRecord]:
    JobsModeratorsFieldsFormGroupKey.singleSelectRecordFields,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray]: JobsModeratorsFieldsFormGroupKey.stringArrayFields,
};

export const fieldHasEmptyValue = (value: FieldValue): boolean => {
  if (value === '' || value === 0 || value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value) && !value.length) {
    return true;
  }
  return typeof value === 'object' && 'min' in value && value.min === '' && !value.max;
};

export const fieldWasNotTouched = (value: FieldValue): boolean => {
  return fieldHasEmptyValue(value) || value === false;
};

const convertRangeValueToNumber = (value: { min: string; max: string }, input: 'max' | 'min'): number | null => {
  if (input === 'min') {
    return Number(value.min);
  }
  if (value.min === value.max) {
    return Number(value.max);
  }
  return Number(value.max) || null;
};

export const getValueIdentifierForFieldType: Record<JobsModeratorsFieldsType, PossibleValueIdentifiers> = {
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloat]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsInteger]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsString]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect]: PossibleValueIdentifiers.values,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord]: PossibleValueIdentifiers.recordIds,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect]: PossibleValueIdentifiers.value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelectRecord]: PossibleValueIdentifiers.recordId,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray]: PossibleValueIdentifiers.values,
};

export const getValueParserForFieldType: Record<JobsModeratorsFieldsType, (value: any) => unknown> = {
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloat]: value => value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsInteger]: value => value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean]: value => value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsString]: value => value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange]: value => {
    return {
      min: convertRangeValueToNumber(value, 'min'),
      max: convertRangeValueToNumber(value, 'max'),
    };
  },
  [JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange]: value => {
    return {
      min: convertRangeValueToNumber(value, 'min'),
      max: convertRangeValueToNumber(value, 'max'),
    };
  },
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect]: value => value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord]: (value: Array<{ id?: string; value?: string }>) =>
    value.map(record => record.id || record),
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect]: value => (Array.isArray(value) ? value[0] : value),
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelectRecord]: value =>
    Array.isArray(value) ? value[0].id || value[0] : value.id || value,
  [JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray]: value => value,
};

export const employmentTypeOptions = [
  { value: JobsVacancyEmployment.FullTime, label: 'Full time employment' },
  { value: JobsVacancyEmployment.SideHustle, label: 'Side-hustle' },
  { value: JobsVacancyEmployment.Contract, label: 'Contract to hire' },
];

export const vacancyFieldValueKey: Record<JobsModeratorsFieldsType, string> = {
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloat]: 'floatValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsInteger]: 'integerValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean]: 'booleanValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsString]: 'stringValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsFloatRange]: 'floatRangeValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsIntegerRange]: 'integerRangeValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect]: 'multiSelectValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord]: 'multiSelectRecordValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect]: 'singleSelectValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelectRecord]: 'singleSelectRecordValue',
  [JobsModeratorsFieldsType.JobsModeratorsFieldsStringArray]: 'stringArrayValue',
};

export const countryOptions = [
  { id: 1, name: 'Afghanistan', label: 'Afghanistan' },
  {
    id: 2,
    name: 'Albania',
    label: 'Albania',
  },
  { id: 3, name: 'Algeria', label: 'Algeria' },
  {
    id: 4,
    name: 'American Samoa',
    label: 'American Samoa',
  },
  { id: 5, name: 'Andorra', label: 'Andorra' },
  { id: 6, name: 'Angola', label: 'Angola' },
  {
    id: 7,
    name: 'Anguilla',
    label: 'Anguilla',
  },
  { id: 8, name: 'Antarctica', label: 'Antarctica' },
  {
    id: 9,
    name: 'Antigua and Barbuda',
    label: 'Antigua and Barbuda',
  },
  { id: 10, name: 'Argentina', label: 'Argentina' },
  {
    id: 11,
    name: 'Armenia',
    label: 'Armenia',
  },
  { id: 12, name: 'Aruba', label: 'Aruba' },
  {
    id: 13,
    name: 'Australia',
    label: 'Australia',
  },
  { id: 14, name: 'Austria', label: 'Austria' },
  {
    id: 15,
    name: 'Azerbaijan',
    label: 'Azerbaijan',
  },
  { id: 16, name: 'Bahamas', label: 'Bahamas' },
  {
    id: 17,
    name: 'Bahrain',
    label: 'Bahrain',
  },
  { id: 18, name: 'Bangladesh', label: 'Bangladesh' },
  {
    id: 19,
    name: 'Barbados',
    label: 'Barbados',
  },
  { id: 20, name: 'Belarus', label: 'Belarus' },
  {
    id: 21,
    name: 'Belgium',
    label: 'Belgium',
  },
  { id: 22, name: 'Belize', label: 'Belize' },
  { id: 23, name: 'Benin', label: 'Benin' },
  {
    id: 24,
    name: 'Bermuda',
    label: 'Bermuda',
  },
  { id: 25, name: 'Bhutan', label: 'Bhutan' },
  {
    id: 26,
    name: 'Bolivia (Plurinational State of)',
    label: 'Bolivia (Plurinational State of)',
  },
  { id: 27, name: 'Bonaire, Sint Eustatius and Saba', label: 'Bonaire, Sint Eustatius and Saba' },
  {
    id: 28,
    name: 'Bosnia and Herzegovina',
    label: 'Bosnia and Herzegovina',
  },
  { id: 29, name: 'Botswana', label: 'Botswana' },
  {
    id: 30,
    name: 'Bouvet Island',
    label: 'Bouvet Island',
  },
  { id: 31, name: 'Brazil', label: 'Brazil' },
  {
    id: 32,
    name: 'British Indian Ocean Territory',
    label: 'British Indian Ocean Territory',
  },
  { id: 33, name: 'Brunei Darussalam', label: 'Brunei Darussalam' },
  {
    id: 34,
    name: 'Bulgaria',
    label: 'Bulgaria',
  },
  { id: 35, name: 'Burkina Faso', label: 'Burkina Faso' },
  {
    id: 36,
    name: 'Burundi',
    label: 'Burundi',
  },
  { id: 37, name: 'Cabo Verde', label: 'Cabo Verde' },
  {
    id: 38,
    name: 'Cambodia',
    label: 'Cambodia',
  },
  { id: 39, name: 'Cameroon', label: 'Cameroon' },
  {
    id: 40,
    name: 'Canada',
    label: 'Canada',
  },
  { id: 41, name: 'Cayman Islands', label: 'Cayman Islands' },
  {
    id: 42,
    name: 'Central African Republic',
    label: 'Central African Republic',
  },
  { id: 43, name: 'Chad', label: 'Chad' },
  { id: 44, name: 'Chile', label: 'Chile' },
  {
    id: 45,
    name: 'China',
    label: 'China',
  },
  { id: 46, name: 'Christmas Island', label: 'Christmas Island' },
  {
    id: 47,
    name: 'Cocos (Keeling) Islands',
    label: 'Cocos (Keeling) Islands',
  },
  { id: 48, name: 'Colombia', label: 'Colombia' },
  {
    id: 49,
    name: 'Comoros',
    label: 'Comoros',
  },
  { id: 50, name: 'Congo', label: 'Congo' },
  {
    id: 51,
    name: 'Congo (Democratic Republic of the)',
    label: 'Congo (Democratic Republic of the)',
  },
  { id: 52, name: 'Cook Islands', label: 'Cook Islands' },
  {
    id: 53,
    name: 'Costa Rica',
    label: 'Costa Rica',
  },
  { id: 54, name: 'Croatia', label: 'Croatia' },
  { id: 55, name: 'Cuba', label: 'Cuba' },
  {
    id: 56,
    name: 'Curaçao',
    label: 'Curaçao',
  },
  { id: 57, name: 'Cyprus', label: 'Cyprus' },
  {
    id: 58,
    name: 'Czech Republic',
    label: 'Czech Republic',
  },
  { id: 59, value: "Côte d'Ivoire", label: "Côte d'Ivoire" },
  {
    id: 60,
    name: 'Denmark',
    label: 'Denmark',
  },
  { id: 61, name: 'Djibouti', label: 'Djibouti' },
  {
    id: 62,
    name: 'Dominica',
    label: 'Dominica',
  },
  { id: 63, name: 'Dominican Republic', label: 'Dominican Republic' },
  {
    id: 64,
    name: 'Ecuador',
    label: 'Ecuador',
  },
  { id: 65, name: 'Egypt', label: 'Egypt' },
  {
    id: 66,
    name: 'El Salvador',
    label: 'El Salvador',
  },
  { id: 67, name: 'Equatorial Guinea', label: 'Equatorial Guinea' },
  {
    id: 68,
    name: 'Eritrea',
    label: 'Eritrea',
  },
  { id: 69, name: 'Estonia', label: 'Estonia' },
  {
    id: 70,
    name: 'Ethiopia',
    label: 'Ethiopia',
  },
  { id: 71, name: 'Falkland Islands (Malvinas)', label: 'Falkland Islands (Malvinas)' },
  {
    id: 72,
    name: 'Faroe Islands',
    label: 'Faroe Islands',
  },
  { id: 73, name: 'Fiji', label: 'Fiji' },
  { id: 74, name: 'Finland', label: 'Finland' },
  {
    id: 75,
    name: 'France',
    label: 'France',
  },
  { id: 76, name: 'French Guiana', label: 'French Guiana' },
  {
    id: 77,
    name: 'French Polynesia',
    label: 'French Polynesia',
  },
  { id: 78, name: 'French Southern Territories', label: 'French Southern Territories' },
  {
    id: 79,
    name: 'Gabon',
    label: 'Gabon',
  },
  { id: 80, name: 'Gambia', label: 'Gambia' },
  { id: 81, name: 'Georgia', label: 'Georgia' },
  {
    id: 82,
    name: 'Germany',
    label: 'Germany',
  },
  { id: 83, name: 'Ghana', label: 'Ghana' },
  {
    id: 84,
    name: 'Gibraltar',
    label: 'Gibraltar',
  },
  { id: 85, name: 'Greece', label: 'Greece' },
  {
    id: 86,
    name: 'Greenland',
    label: 'Greenland',
  },
  { id: 87, name: 'Grenada', label: 'Grenada' },
  {
    id: 88,
    name: 'Guadeloupe',
    label: 'Guadeloupe',
  },
  { id: 89, name: 'Guam', label: 'Guam' },
  { id: 90, name: 'Guatemala', label: 'Guatemala' },
  {
    id: 91,
    name: 'Guernsey',
    label: 'Guernsey',
  },
  { id: 92, name: 'Guinea', label: 'Guinea' },
  {
    id: 93,
    name: 'Guinea-Bissau',
    label: 'Guinea-Bissau',
  },
  { id: 94, name: 'Guyana', label: 'Guyana' },
  { id: 95, name: 'Haiti', label: 'Haiti' },
  {
    id: 96,
    name: 'Heard Island and McDonald Islands',
    label: 'Heard Island and McDonald Islands',
  },
  { id: 97, name: 'Holy See', label: 'Holy See' },
  {
    id: 98,
    name: 'Honduras',
    label: 'Honduras',
  },
  { id: 99, name: 'Hong Kong', label: 'Hong Kong' },
  {
    id: 100,
    name: 'Hungary',
    label: 'Hungary',
  },
  { id: 101, name: 'Iceland', label: 'Iceland' },
  {
    id: 102,
    name: 'India',
    label: 'India',
  },
  { id: 103, name: 'Indonesia', label: 'Indonesia' },
  {
    id: 104,
    name: 'Iran (Islamic Republic of)',
    label: 'Iran (Islamic Republic of)',
  },
  { id: 105, name: 'Iraq', label: 'Iraq' },
  { id: 106, name: 'Ireland', label: 'Ireland' },
  {
    id: 107,
    name: 'Isle of Man',
    label: 'Isle of Man',
  },
  { id: 108, name: 'Israel', label: 'Israel' },
  { id: 109, name: 'Italy', label: 'Italy' },
  {
    id: 110,
    name: 'Jamaica',
    label: 'Jamaica',
  },
  { id: 111, name: 'Japan', label: 'Japan' },
  { id: 112, name: 'Jersey', label: 'Jersey' },
  {
    id: 113,
    name: 'Jordan',
    label: 'Jordan',
  },
  { id: 114, name: 'Kazakhstan', label: 'Kazakhstan' },
  {
    id: 115,
    name: 'Kenya',
    label: 'Kenya',
  },
  { id: 116, name: 'Kiribati', label: 'Kiribati' },
  {
    id: 117,
    value: "Korea (Democratic People's Republic of)",
    label: "Korea (Democratic People's Republic of)",
  },
  { id: 118, name: 'Korea (Republic of)', label: 'Korea (Republic of)' },
  {
    id: 119,
    name: 'Kuwait',
    label: 'Kuwait',
  },
  { id: 120, name: 'Kyrgyzstan', label: 'Kyrgyzstan' },
  {
    id: 121,
    value: "Lao People's Democratic Republic",
    label: "Lao People's Democratic Republic",
  },
  { id: 122, name: 'Latvia', label: 'Latvia' },
  {
    id: 123,
    name: 'Lebanon',
    label: 'Lebanon',
  },
  { id: 124, name: 'Lesotho', label: 'Lesotho' },
  {
    id: 125,
    name: 'Liberia',
    label: 'Liberia',
  },
  { id: 126, name: 'Libya', label: 'Libya' },
  {
    id: 127,
    name: 'Liechtenstein',
    label: 'Liechtenstein',
  },
  { id: 128, name: 'Lithuania', label: 'Lithuania' },
  {
    id: 129,
    name: 'Luxembourg',
    label: 'Luxembourg',
  },
  { id: 130, name: 'Macao', label: 'Macao' },
  {
    id: 131,
    name: 'Madagascar',
    label: 'Madagascar',
  },
  { id: 132, name: 'Malawi', label: 'Malawi' },
  {
    id: 133,
    name: 'Malaysia',
    label: 'Malaysia',
  },
  { id: 134, name: 'Maldives', label: 'Maldives' },
  {
    id: 135,
    name: 'Mali',
    label: 'Mali',
  },
  { id: 136, name: 'Malta', label: 'Malta' },
  {
    id: 137,
    name: 'Marshall Islands',
    label: 'Marshall Islands',
  },
  { id: 138, name: 'Martinique', label: 'Martinique' },
  {
    id: 139,
    name: 'Mauritania',
    label: 'Mauritania',
  },
  { id: 140, name: 'Mauritius', label: 'Mauritius' },
  {
    id: 141,
    name: 'Mayotte',
    label: 'Mayotte',
  },
  { id: 142, name: 'Mexico', label: 'Mexico' },
  {
    id: 143,
    name: 'Micronesia (Federated States of)',
    label: 'Micronesia (Federated States of)',
  },
  { id: 144, name: 'Moldova (Republic of)', label: 'Moldova (Republic of)' },
  {
    id: 145,
    name: 'Monaco',
    label: 'Monaco',
  },
  { id: 146, name: 'Mongolia', label: 'Mongolia' },
  {
    id: 147,
    name: 'Montenegro',
    label: 'Montenegro',
  },
  { id: 148, name: 'Montserrat', label: 'Montserrat' },
  {
    id: 149,
    name: 'Morocco',
    label: 'Morocco',
  },
  { id: 150, name: 'Mozambique', label: 'Mozambique' },
  {
    id: 151,
    name: 'Myanmar',
    label: 'Myanmar',
  },
  { id: 152, name: 'Namibia', label: 'Namibia' },
  {
    id: 153,
    name: 'Nauru',
    label: 'Nauru',
  },
  { id: 154, name: 'Nepal', label: 'Nepal' },
  {
    id: 155,
    name: 'Netherlands',
    label: 'Netherlands',
  },
  { id: 156, name: 'New Caledonia', label: 'New Caledonia' },
  {
    id: 157,
    name: 'New Zealand',
    label: 'New Zealand',
  },
  { id: 158, name: 'Nicaragua', label: 'Nicaragua' },
  {
    id: 159,
    name: 'Niger',
    label: 'Niger',
  },
  { id: 160, name: 'Nigeria', label: 'Nigeria' },
  { id: 161, name: 'Niue', label: 'Niue' },
  {
    id: 162,
    name: 'Norfolk Island',
    label: 'Norfolk Island',
  },
  { id: 163, name: 'North Macedonia', label: 'North Macedonia' },
  {
    id: 164,
    name: 'Northern Mariana Islands',
    label: 'Northern Mariana Islands',
  },
  { id: 165, name: 'Norway', label: 'Norway' },
  { id: 166, name: 'Oman', label: 'Oman' },
  {
    id: 167,
    name: 'Pakistan',
    label: 'Pakistan',
  },
  { id: 168, name: 'Palau', label: 'Palau' },
  {
    id: 169,
    name: 'Palestine, State of',
    label: 'Palestine, State of',
  },
  { id: 170, name: 'Panama', label: 'Panama' },
  {
    id: 171,
    name: 'Papua New Guinea',
    label: 'Papua New Guinea',
  },
  { id: 172, name: 'Paraguay', label: 'Paraguay' },
  {
    id: 173,
    name: 'Peru',
    label: 'Peru',
  },
  { id: 174, name: 'Philippines', label: 'Philippines' },
  {
    id: 175,
    name: 'Pitcairn',
    label: 'Pitcairn',
  },
  { id: 176, name: 'Poland', label: 'Poland' },
  {
    id: 177,
    name: 'Portugal',
    label: 'Portugal',
  },
  { id: 178, name: 'Puerto Rico', label: 'Puerto Rico' },
  {
    id: 179,
    name: 'Qatar',
    label: 'Qatar',
  },
  { id: 180, name: 'Romania', label: 'Romania' },
  {
    id: 181,
    name: 'Russian Federation',
    label: 'Russian Federation',
  },
  { id: 182, name: 'Rwanda', label: 'Rwanda' },
  {
    id: 183,
    name: 'Réunion',
    label: 'Réunion',
  },
  { id: 184, name: 'Saint Barthélemy', label: 'Saint Barthélemy' },
  {
    id: 185,
    name: 'Saint Helena, Ascension and Tristan da Cunha',
    label: 'Saint Helena, Ascension and Tristan da Cunha',
  },
  { id: 186, name: 'Saint Kitts and Nevis', label: 'Saint Kitts and Nevis' },
  {
    id: 187,
    name: 'Saint Lucia',
    label: 'Saint Lucia',
  },
  { id: 188, name: 'Saint Martin (French part)', label: 'Saint Martin (French part)' },
  {
    id: 189,
    name: 'Saint Pierre and Miquelon',
    label: 'Saint Pierre and Miquelon',
  },
  { id: 190, name: 'Saint Vincent and the Grenadines', label: 'Saint Vincent and the Grenadines' },
  {
    id: 191,
    name: 'Samoa',
    label: 'Samoa',
  },
  { id: 192, name: 'San Marino', label: 'San Marino' },
  {
    id: 193,
    name: 'Sao Tome and Principe',
    label: 'Sao Tome and Principe',
  },
  { id: 194, name: 'Saudi Arabia', label: 'Saudi Arabia' },
  {
    id: 195,
    name: 'Senegal',
    label: 'Senegal',
  },
  { id: 196, name: 'Serbia', label: 'Serbia' },
  {
    id: 197,
    name: 'Seychelles',
    label: 'Seychelles',
  },
  { id: 198, name: 'Sierra Leone', label: 'Sierra Leone' },
  {
    id: 199,
    name: 'Singapore',
    label: 'Singapore',
  },
  { id: 200, name: 'Sint Maarten (Dutch part)', label: 'Sint Maarten (Dutch part)' },
  {
    id: 201,
    name: 'Slovakia',
    label: 'Slovakia',
  },
  { id: 202, name: 'Slovenia', label: 'Slovenia' },
  {
    id: 203,
    name: 'Solomon Islands',
    label: 'Solomon Islands',
  },
  { id: 204, name: 'Somalia', label: 'Somalia' },
  {
    id: 205,
    name: 'South Africa',
    label: 'South Africa',
  },
  {
    id: 206,
    name: 'South Georgia and the South Sandwich Islands',
    label: 'South Georgia and the South Sandwich Islands',
  },
  { id: 207, name: 'South Sudan', label: 'South Sudan' },
  {
    id: 208,
    name: 'Spain',
    label: 'Spain',
  },
  { id: 209, name: 'Sri Lanka', label: 'Sri Lanka' },
  {
    id: 210,
    name: 'Sudan',
    label: 'Sudan',
  },
  { id: 211, name: 'Suriname', label: 'Suriname' },
  {
    id: 212,
    name: 'Svalbard and Jan Mayen',
    label: 'Svalbard and Jan Mayen',
  },
  { id: 213, name: 'Sweden', label: 'Sweden' },
  {
    id: 214,
    name: 'Switzerland',
    label: 'Switzerland',
  },
  { id: 215, name: 'Syrian Arab Republic', label: 'Syrian Arab Republic' },
  {
    id: 216,
    name: 'Taiwan, Province of China',
    label: 'Taiwan, Province of China',
  },
  { id: 217, name: 'Tajikistan', label: 'Tajikistan' },
  {
    id: 218,
    name: 'Tanzania, United Republic of',
    label: 'Tanzania, United Republic of',
  },
  { id: 219, name: 'Thailand', label: 'Thailand' },
  {
    id: 220,
    name: 'Timor-Leste',
    label: 'Timor-Leste',
  },
  { id: 221, name: 'Togo', label: 'Togo' },
  { id: 222, name: 'Tokelau', label: 'Tokelau' },
  {
    id: 223,
    name: 'Tonga',
    label: 'Tonga',
  },
  { id: 224, name: 'Trinidad and Tobago', label: 'Trinidad and Tobago' },
  {
    id: 225,
    name: 'Tunisia',
    label: 'Tunisia',
  },
  { id: 226, name: 'Turkey', label: 'Turkey' },
  {
    id: 227,
    name: 'Turkmenistan',
    label: 'Turkmenistan',
  },
  { id: 228, name: 'Turks and Caicos Islands', label: 'Turks and Caicos Islands' },
  {
    id: 229,
    name: 'Tuvalu',
    label: 'Tuvalu',
  },
  { id: 230, name: 'Uganda', label: 'Uganda' },
  {
    id: 231,
    name: 'Ukraine',
    label: 'Ukraine',
  },
  { id: 232, name: 'United Arab Emirates', label: 'United Arab Emirates' },
  {
    id: 233,
    name: 'United Kingdom of Great Britain and Northern Ireland',
    label: 'United Kingdom of Great Britain and Northern Ireland',
  },
  {
    id: 234,
    name: 'United States Minor Outlying Islands',
    label: 'United States Minor Outlying Islands',
  },
  { id: 235, name: 'United States of America', label: 'United States of America' },
  {
    id: 236,
    name: 'Uruguay',
    label: 'Uruguay',
  },
  { id: 237, name: 'Uzbekistan', label: 'Uzbekistan' },
  {
    id: 238,
    name: 'Vanuatu',
    label: 'Vanuatu',
  },
  {
    id: 239,
    name: 'Venezuela (Bolivarian Republic of)',
    label: 'Venezuela (Bolivarian Republic of)',
  },
  { id: 240, name: 'Viet Nam', label: 'Viet Nam' },
  {
    id: 241,
    name: 'Virgin Islands (British)',
    label: 'Virgin Islands (British)',
  },
  { id: 242, name: 'Virgin Islands (U.S.)', label: 'Virgin Islands (U.S.)' },
  {
    id: 243,
    name: 'Wallis and Futuna',
    label: 'Wallis and Futuna',
  },
  { id: 244, name: 'Western Sahara', label: 'Western Sahara' },
  {
    id: 245,
    name: 'Yemen',
    label: 'Yemen',
  },
  { id: 246, name: 'Zambia', label: 'Zambia' },
  {
    id: 247,
    name: 'Zimbabwe',
    label: 'Zimbabwe',
  },
  { id: 248, name: 'eSwatini', label: 'eSwatini' },
  {
    id: 249,
    name: 'Åland Islands',
    label: 'Åland Islands',
  },
];
